import {
	type Match,
	type RenderErrorArgs,
	type RenderReadyArgs,
	type Router,
	type RouterState,
	withRouter,
} from 'found';
import React, { useContext, useRef } from 'react';

interface RouterContextValue {
	router: Router;
	match: Match;
}

export const RouterContext = React.createContext<RouterContextValue>({
	router: {} as Router,
	match: {} as Match,
});

// found 1.3 sets its router context to the render args of *every* resolver
// iteration, pending ones included, where found 0.3 only ever published the
// match it had already resolved. Consumers that read the match outside an event
// handler therefore see destinations the user may never reach. This is found's
// own resolved predicate (`createBaseRouter.js:160`) applied to the value: the
// `elements` key is always present and merely undefined while pending, so an
// `in` check would filter nothing.
const isResolved = (match: Match | undefined) => {
	const renderArgs = match as Partial<RenderReadyArgs & RenderErrorArgs> | undefined;
	return Boolean(renderArgs?.elements || renderArgs?.error);
};

export const useResolvedMatch = (match: Match) => {
	const lastResolved = useRef<Match | undefined>(undefined);
	if (isResolved(match)) {
		lastResolved.current = match;
	}
	return lastResolved.current ?? match;
};

/**
 * Applied immediately after `withRouter` — never instead of it — wherever a
 * component reads the injected `match` outside an event handler. `withRouter`
 * still does the injecting; this only holds back the pending values found 1.3
 * publishes. Components that read the match through `useRouter()` need nothing:
 * `RouterProvider` below is already filtered.
 */
export const withResolvedMatch =
	<P extends RouterState>(Component: React.ComponentType<P>) =>
	(props: P) => {
		const match = useResolvedMatch(props.match);
		return <Component {...props} match={match} />;
	};

export const RouterProvider = withRouter(
	withResolvedMatch((props: RouterState & { children?: React.ReactNode }) => {
		return (
			<RouterContext.Provider
				value={{
					router: props.router,
					match: props.match,
				}}
			>
				{props.children}
			</RouterContext.Provider>
		);
	})
);

/**
 * This is added as a DIY implementation of the found >= v0.4 hooks method
 * `useRouter`. This is added as the upgrade of found to 0.4.x created too
 * many issues, and the process of migrating to react-router is itself
 * quite costly.
 *
 * If and when we either:
 *   * Upgrade found
 *   * Migrate to react-router
 *
 * The we should be able to replace this function with the implementations
 * provided by these libraries, the functionality of which should be the same
 * (with the noted exception that react-router uses useHistory, useMatch etc
 * rather than useRouter)
 */
const useRouter = () => {
	return useContext(RouterContext);
};

export default useRouter;
